import {StatCardData} from '../../../util/interfaces.ts';
import {useEffect, useMemo, useState} from 'react';
import {StatCardDataType} from '../../../util/enums.ts';
import BolmateStatCard from './bolmateStatCard.tsx';

interface Props {
    data: StatCardData | Array<StatCardData>;
    className?: string;
    tooltip?: string;
    /** Names the window the card compares against, e.g. "Dag ervoor". Defaults
     * to a plain "Vorige" for views that have no period notion. */
    comparisonLabel?: string;
    /** Drops the trend icon, change badge and previous-value footer — for
     * periods that have no window before them ("Alles"). */
    hideComparison?: boolean;
}

export default function BolmateStatCardController(props: Props) {
    const {data} = props;
    const [visibleData, setVisibleData] = useState<StatCardData | null>(
        Array.isArray(data) ? (data.length ? data[0] : null) : data
    );
    const [visibleDataType, setVisibleDataType] = useState<StatCardDataType | 'original'>('original');
    const renderData: StatCardData | null = useMemo(() => {
        if (!visibleData) return null;
        if (visibleDataType === 'original') return visibleData;
        const alternate = visibleData.alternates?.find(
            (alternate: StatCardData) => alternate.valueType === visibleDataType
        );
        if (!alternate && visibleDataType === StatCardDataType.PERCENTAGE)
            // Generic percentage swap
            return {...visibleData, swapValuePercentage: true};
        return (
            visibleData.alternates?.find((alternate: StatCardData) => alternate.valueType === visibleDataType) ??
            visibleData
        );
    }, [visibleData, visibleDataType]);

    function onSwapData() {
        if (!visibleData || !Array.isArray(data)) return;
        const currentIndex = data.indexOf(visibleData);
        if (currentIndex === -1) return;
        const nextIndex = (currentIndex + 1) % data.length;
        setVisibleData(data[nextIndex]);
        setVisibleDataType('original');
    }

    function onChangeDataType(dataType: StatCardDataType) {
        return function () {
            setVisibleDataType(visibleDataType === dataType ? 'original' : dataType);
        };
    }

    useEffect(() => {
        setVisibleData(Array.isArray(data) ? (data.length ? data[0] : null) : data);
        setVisibleDataType('original');
    }, [data]);

    if (!renderData) return null;

    return (
        <BolmateStatCard
            {...props}
            onSwapData={onSwapData}
            onChangeDataType={onChangeDataType}
            data={renderData}
            hasMultipleData={Array.isArray(data) && data.length > 1}
            visibleDataType={visibleDataType}
        />
    );
}
